// File: src/components/OmniCine/Timeline/XR/XRMarkerIntentBridge.tsx
//
// XRMarkerIntentBridge (Phase N2 — Option 2 posture)
// --------------------------------------------------
// Current target posture:
// - XR is allowed to sculpt lane-scoped envelope ranges (handled by XRRangeIntentBridge)
// - XR markers are DISABLED (envelopes-only workflow)
//
// This bridge remains mountable, but is inert-by-default via XRSessionAuthority.
// - It NEVER mutates reducer logic; it only emits existing marker actions when enabled.
// - StrictMode-safe: would clean up orphan active markers on unmount if ever enabled.
//
// To enable markers later (intentionally), flip XR_AUTH.enabled to true and (optionally)
// tighten by mode/token requirements in XRSessionAuthority.

import React, { useEffect, useMemo, useRef } from "react";
import { useOmniCineTimeline } from "@/state/useOmniCineTimeline";
import { XRSessionAuthority } from "@/xr/session/XRSessionAuthority";

/* ------------------------------------------------------------------ */
/* Authority (module singleton)                                        */
/* ------------------------------------------------------------------ */

const XR_AUTH = new XRSessionAuthority("standalone", {
  // ✅ Option 2 posture: markers disabled (envelopes-only)
  enabled: false,

  // If you ever enable later:
  requireToken: false,

  // Narrow allow-list (used only if enabled: true)
  allowedOps: ["marker/begin", "marker/update", "marker/remove"],

  // Silent by default (safe). Uncomment for diagnostics.
  onDeny: (_reason, _intent) => {
    // console.warn("[XR_MARKER_AUTH] deny:", _reason, _intent);
  },
});

/* ------------------------------------------------------------------ */
/* Types                                                               */
/* ------------------------------------------------------------------ */

type XRMarkerIntentDetail = {
  controllerId: string;
  timeSec: number;
  label?: string;
  source?: string; // "xr-user" recommended
};

const EVT_ADD = "omnicine:xr:marker:add";
const EVT_MOVE = "omnicine:xr:marker:move";
const EVT_REMOVE = "omnicine:xr:marker:remove";

/* ------------------------------------------------------------------ */
/* Utilities                                                           */
/* ------------------------------------------------------------------ */

function clamp(n: number, min: number, max: number) {
  return Math.max(min, Math.min(max, n));
}

function toNum(x: unknown, fallback = NaN) {
  const n = Number(x);
  return Number.isFinite(n) ? n : fallback;
}

function uuid() {
  try {
    const c = globalThis.crypto as Crypto | undefined;
    if (c?.randomUUID) return c.randomUUID();
  } catch {
    // ignore
  }
  return `mk_${Date.now().toString(36)}_${Math.random().toString(36).slice(2, 10)}`;
}

function normalizeSource(raw: unknown): "user" | "xr-user" | "ai-assist" {
  const s = String(raw ?? "xr-user").toLowerCase().trim();
  if (s === "ai-assist") return "ai-assist";
  if (s === "user") return "user";
  return "xr-user";
}

function normalizeDetail(raw: unknown): XRMarkerIntentDetail | null {
  if (!raw || typeof raw !== "object") return null;
  const r = raw as Record<string, unknown>;

  const controllerId = String(r.controllerId ?? "").trim();
  if (!controllerId) return null;

  const timeSec = toNum(r.timeSec);
  if (!Number.isFinite(timeSec)) return null;

  const label = r.label != null ? String(r.label) : undefined;
  const source = r.source != null ? String(r.source) : "xr-user";

  return { controllerId, timeSec, label, source };
}

/* ------------------------------------------------------------------ */
/* Component                                                           */
/* ------------------------------------------------------------------ */

export default function XRMarkerIntentBridge(): null {
  const { state, dispatch } = useOmniCineTimeline();

  // One active marker id per controllerId (supports add->move)
  const activeByController = useRef<Map<string, string>>(new Map());

  // Keep duration fresh without rebinding listeners
  const stateRef = useRef(state);
  useEffect(() => {
    stateRef.current = state;
  }, [state]);

  const allow = useMemo(() => {
    return (
      op: "marker/begin" | "marker/update" | "marker/remove",
      controllerId: string,
    ): boolean => {
      // Register is safe even when disabled; validate will hard-deny if disabled.
      XR_AUTH.registerClient(controllerId);

      return XR_AUTH.validateXRIntent({
        op,
        meta: { source: "xr-user", clientId: controllerId },
      });
    };
  }, []);

  // StrictMode-safe cleanup: remove active markers if bridge unmounts mid-gesture
  useEffect(() => {
    return () => {
      const active = activeByController.current;
      if (!active.size) return;

      for (const [, markerId] of active.entries()) {
        dispatch({
          type: "marker/remove",
          payload: { markerId },
          meta: { source: "xr-user", note: "xr marker bridge unmount cleanup" },
        } as any);
      }

      active.clear();
    };
  }, [dispatch]);

  const api = useMemo(() => {
    const duration = () => stateRef.current.durationSec;

    const add = (d: XRMarkerIntentDetail) => {
      if (!allow("marker/begin", d.controllerId)) return;

      const dur = duration();
      const t = clamp(d.timeSec, 0, dur);

      const existingId = activeByController.current.get(d.controllerId);
      if (existingId) {
        dispatch({
          type: "marker/move",
          payload: { markerId: existingId, timeSec: t },
          meta: { source: d.source ?? "xr-user", transient: true, note: "xr marker add->move" },
        } as any);
        return;
      }

      const id = uuid();
      const fps = Math.max(1, Number((stateRef.current as any)?.snap?.fps ?? 30) || 30);
      activeByController.current.set(d.controllerId, id);

      dispatch({
        type: "marker/add",
        payload: {
          marker: {
            id,
            timeSec: t,
            label: d.label,
            type: "note",
            note: d.label?.trim() || null,
            description: d.label?.trim() || null,
            frameIndex: Math.max(0, Math.round(t * fps)),
            visible: true,
            source: normalizeSource(d.source),
          } as any,
        },
        meta: { source: d.source ?? "xr-user", transient: true, note: "xr marker add" },
      } as any);
    };

    const move = (d: XRMarkerIntentDetail) => {
      if (!allow("marker/update", d.controllerId)) return;

      const dur = duration();
      const t = clamp(d.timeSec, 0, dur);

      const id = activeByController.current.get(d.controllerId);
      if (!id) {
        // move without add -> fail-safe add (still gated)
        add(d);
        return;
      }

      dispatch({
        type: "marker/move",
        payload: { markerId: id, timeSec: t },
        meta: { source: d.source ?? "xr-user", transient: true, note: "xr marker move" },
      } as any);
    };

    const remove = (d: XRMarkerIntentDetail) => {
      if (!allow("marker/remove", d.controllerId)) return;

      const id = activeByController.current.get(d.controllerId);
      if (!id) return;

      activeByController.current.delete(d.controllerId);

      dispatch({
        type: "marker/remove",
        payload: { markerId: id },
        meta: { source: d.source ?? "xr-user", note: "xr marker remove" },
      } as any);
    };

    return { add, move, remove };
  }, [allow, dispatch]);

  useEffect(() => {
    const onAdd = (ev: Event) => {
      const d = normalizeDetail((ev as CustomEvent).detail);
      if (!d) return;
      api.add(d);
    };

    const onMove = (ev: Event) => {
      const d = normalizeDetail((ev as CustomEvent).detail);
      if (!d) return;
      api.move(d);
    };

    const onRemove = (ev: Event) => {
      const d = normalizeDetail((ev as CustomEvent).detail);
      if (!d) return;
      api.remove(d);
    };

    window.addEventListener(EVT_ADD, onAdd as EventListener);
    window.addEventListener(EVT_MOVE, onMove as EventListener);
    window.addEventListener(EVT_REMOVE, onRemove as EventListener);

    return () => {
      window.removeEventListener(EVT_ADD, onAdd as EventListener);
      window.removeEventListener(EVT_MOVE, onMove as EventListener);
      window.removeEventListener(EVT_REMOVE, onRemove as EventListener);
    };
  }, [api]);

  return null;
}
